/* global gdAlertify */
import React, { useState } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatDate, formatMoney } from "../utils/utils";
import CobranzaDetalle from "./CobranzaDetalle";
import CobranzaAjusteModal from "./CobranzaAjusteModal";
import CobranzaAdelantoModal from "./CobranzaAdelantoModal";
import CobranzaFacturas from "./CobranzaFacturas";
import CartolaPacienteModal from "./components/CartolaPacienteModal";

const Cobranza = () => {
    const [fecha, setFecha] = useState("");
    const [resultados, setResultados] = useState([]);
    const [vistaActiva, setVistaActiva] = useState("general");

    const [isLiquidado, setIsLiquidado] = useState(false);

    const [showDetalle, setShowDetalle] = useState(false);
    const [showAjusteModal, setShowAjusteModal] = useState(false);
    const [showAdelantoModal, setShowAdelantoModal] = useState(false);
    const [showCartolaModal, setShowCartolaModal] = useState(false);
    const [selectedPacienteCartola, setSelectedPacienteCartola] = useState(null);
    const [detallePacienteID, setDetallePacienteID] = useState(null);
    const [detallePacienteNombre, setDetallePacienteNombre] = useState("");
    const [detalleCargos, setDetalleCargos] = useState(0);
    const [detalleDescuentos, setDetalleDescuentos] = useState(0);
    const [detalleObservacionAjuste, setDetalleObservacionAjuste] = useState("");
    const [modoNotificacion, setModoNotificacion] = useState(false);
    const [totalPagar, setTotalPagar] = useState(0);

    const buscarCobranza = async () => {
        if (!fecha) return;
        const [anio, mes] = fecha.split("-");
        try {
            const params = { fecha };
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza", { params });
            setResultados(response.data.data);
            setIsLiquidado(response.data.liquidado || false);
        } catch (error) {
            console.error("Error al buscar cobranza:", error);
        }
    };

    const columns = [
        {
            title: "Acciones",
            name: "acciones",
            render: row => (
                <div className="dropdown">
                    <button
                        className="btn btn-sm btn-secondary dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        Acciones
                    </button>
                    <ul className="dropdown-menu">
                        <li>
                            <button 
                                className="dropdown-item text-primary"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setTotalPagar(
                                        (Number(row.total_general) || 0) + 
                                        (Number(row.saldo_anterior) || 0) + 
                                        (Number(row.otros_suma) || 0) - 
                                        (Number(row.otros_resta) || 0)
                                    );
                                    setModoNotificacion(false);
                                    setShowDetalle(true);
                                }}
                            >
                                <i className="bi bi-eye me-2"></i> Ver Detalle
                            </button>
                        </li>
                        <li>
                            <button 
                                className="dropdown-item text-success"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setTotalPagar(
                                        (Number(row.total_general) || 0) + 
                                        (Number(row.saldo_anterior) || 0) + 
                                        (Number(row.otros_suma) || 0) - 
                                        (Number(row.otros_resta) || 0)
                                    );
                                    setModoNotificacion(true);
                                    setShowDetalle(true);
                                }}
                            >
                                <i className="bi bi-envelope me-2"></i> Notificar al Paciente
                            </button>
                        </li>

                        <li>
                            <button 
                                className="dropdown-item text-warning text-dark mt-1"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setDetalleCargos(Number(row.otros_suma) || 0);
                                    setDetalleDescuentos(Number(row.otros_resta) || 0);
                                    setDetalleObservacionAjuste(row.observacion || "");
                                    setShowAjusteModal(true);
                                }}
                                disabled={isLiquidado}
                            >
                                <i className="bi bi-sliders me-2"></i> {isLiquidado ? "Ajustes (Desactivado)" : "Ajustes (Suma/Resta)"}
                            </button>
                        </li>
                        <li>
                            <button 
                                className="dropdown-item text-secondary mt-1"
                                onClick={() => {
                                    setDetallePacienteID(row.paciente_id);
                                    setDetallePacienteNombre(row.nombre_paciente);
                                    setShowAdelantoModal(true);
                                }}
                                disabled={isLiquidado}
                            >
                                <i className="bi bi-calendar-check me-2"></i> {isLiquidado ? "Adelantos (Desactivado)" : "Adelantos (Sig. Mes)"}
                            </button>
                        </li>
                        <li>
                            <button 
                                className="dropdown-item text-info mt-1 fw-bold"
                                onClick={() => {
                                    setSelectedPacienteCartola(row);
                                    setShowCartolaModal(true);
                                }}
                            >
                                <i className="bi bi-person-lines-fill me-2"></i> Ver Cartola
                            </button>
                        </li>
                    </ul>
                </div>
            )
        },
        { title: "RUT", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        {
            title: "Estado",
            name: "estado_paciente",
            render: row => (
                <div className="d-flex flex-column align-items-start">
                    {row.estado_paciente == 1 ? (
                        <span className="badge bg-success">Activo</span>
                    ) : (
                        <span className="badge bg-danger">Retirado</span>
                    )}
                    {row.estado_paciente != 1 && row.fecha_retiro && (
                        <small className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                            Retiro: {formatDate(row.fecha_retiro)}
                        </small>
                    )}
                </div>
            )
        },
        { 
            title: "Saldo Anterior", 
            name: "saldo_anterior", 
            type: "number",
            render: row => <span className="text-warning fw-bold">{Number(row.saldo_anterior) > 0 ? `$${formatMoney(row.saldo_anterior)}` : '$0'}</span>
        },
        { 
            title: "Otros Suma", 
            name: "otros_suma", 
            type: "number",
            render: row => <span className="text-danger">{Number(row.otros_suma) > 0 ? `$${formatMoney(row.otros_suma)}` : '$0'}</span>
        },
        { 
            title: "Otros Resta", 
            name: "otros_resta", 
            type: "number",
            render: row => <span className="text-success">{Number(row.otros_resta) > 0 ? `$${formatMoney(row.otros_resta)}` : '$0'}</span>
        },
        { 
            title: "Observación", 
            name: "observacion", 
            render: row => <span className="text-muted small">{row.observacion || '-'}</span>
        },
        { 
            title: "Total a Cobrar", 
            name: "deuda_total_computada", 
            type: "number",
            render: row => {
                const total = (Number(row.total_general) || 0) + (Number(row.otros_suma) || 0) - (Number(row.otros_resta) || 0);
                return <span className="fw-bold fs-6">{`$${formatMoney(total)}`}</span>;
            }
        },
        { 
            title: "N° Notificación", 
            name: "numero_notificacion", 
            cellClassName: "text-center",
            headerClassName: "text-center",
            render: row => row.numero_notificacion ? `${row.numero_notificacion}` : "Sin Notificar" 
        },
        {   title: "Última Notificación",
            name: "fecha_ultima_notificacion",
            cellClassName: "text-center",
            headerClassName: "text-center",
            render: row => row.fecha_ultima_notificacion ? formatDate(row.fecha_ultima_notificacion) : "-" 
        },
        { 
            title: "N° Turnos", 
            name: "cantidad_turnos",
            type: "number",
            render: row => (
                <div className="d-flex align-items-center">
                    {row.cantidad_turnos}
                    {row.turnos_siguiente_periodo && row.turnos_siguiente_periodo.length > 0 && (
                        <i className="bi bi-info-circle text-primary ms-2" title={`Incluye ${row.turnos_siguiente_periodo.length} turnos(s) adelantado(s)`}></i>
                    )}
                </div>
            )
        },
        {
            title: "Total Turno",
            name: "total_turno",
            type: "number",
            render: row => {
                const total = (Number(row.valor_turnos_total) || 0) +
                              (Number(row.adicionales_turno_total) || 0) +
                              (Number(row.recargo_fs_turno_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Colación",
            name: "total_colacion",
            type: "number",
            render: row => {
                const total = (Number(row.colacion_total) || 0) +
                              (Number(row.adicionales_colacion_total) || 0) +
                              (Number(row.recargo_fs_colacion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Movilización",
            name: "total_locomocion",
            type: "number",
            render: row => {
                const total = (Number(row.locomocion_total) || 0) +
                              (Number(row.adicionales_locomocion_total) || 0) +
                              (Number(row.recargo_fs_locomocion_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Otros / Extras",
            name: "total_otros",
            type: "number",
            render: row => {
                const total = (Number(row.otro_total) || 0) +
                              (Number(row.adicionales_otro_total) || 0) +
                              (Number(row.recargo_fs_otro_total) || 0) +
                              (Number(row.horas_extras_total) || 0) +
                              (Number(row.adicionales_horas_extras_total) || 0);
                return `$${formatMoney(total)}`;
            }
        },
        {
            title: "Total Adicionales",
            name: "total_adicionales_solo",
            type: "number",
            render: row => {
                const total = (Number(row.adicionales_turno_total) || 0) +
                              (Number(row.adicionales_colacion_total) || 0) +
                              (Number(row.adicionales_locomocion_total) || 0) +
                              (Number(row.adicionales_otro_total) || 0) +
                              (Number(row.adicionales_horas_extras_total) || 0) +
                              (Number(row.recargo_fs_turno_total) || 0) +
                              (Number(row.recargo_fs_colacion_total) || 0) +
                              (Number(row.recargo_fs_locomocion_total) || 0) +
                              (Number(row.recargo_fs_otro_total) || 0);
                return <span className="fw-bold text-primary">{total > 0 ? `$${formatMoney(total)}` : '$0'}</span>;
            }
        },
        { 
            title: "Total Deuda", 
            name: "saldo_cartola", 
            type: "number",
            render: row => (
                <span className={`fw-bold ${Number(row.saldo_cartola) > 0 ? 'text-danger' : 'text-primary'}`}>
                    {`$${formatMoney(row.saldo_cartola || 0)}`}
                </span>
            )
        }
    ];

    const resultadosFiltrados = resultados;

    return (
        <div className="container-fluid mt-4">
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h3 className="mb-0">Cobranza</h3>
                        {isLiquidado && (
                            <span className="badge bg-danger rounded-pill px-3 py-2 fs-6 shadow-sm">
                                <i className="bi bi-lock-fill me-2"></i>
                                Periodo Cerrado / Liquidado
                            </span>
                        )}
                    </div>
                    
                    <div className="row g-3 align-items-end">
                        <div className="col-md-6">
                            <label className="form-label">Mes</label>
                            <input
                                type="month"
                                className="form-control"
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-primary w-100"
                                onClick={buscarCobranza}
                                disabled={!fecha}
                            >
                                Buscar
                            </button>
                        </div>
                        <div className="col-md-4">
                            {!isLiquidado && resultados.length > 0 && (
                                <button
                                    className="btn btn-warning text-dark w-100 fw-bold border border-warning"
                                    onClick={() => {
                                        gdAlertify.confirm({
                                            title: `¿Liquidar periodo ${fecha}?`,
                                            message: "Se generará una foto exacta inmodificable de toda la cobranza actual. Ya no podrás agregar ajustes o adelantos a este mes.",
                                            onConfirm: async () => {
                                                try {
                                                    await axiosInstance.post('/acuerdo-mutuo/cobranza/liquidar', { periodo: fecha });
                                                    gdAlertify.notiSuccess("El periodo ha sido cerrado con éxito.");
                                                    buscarCobranza();
                                                } catch (e) {
                                                    gdAlertify.notiError("No se pudo liquidar este periodo.");
                                                }
                                            },
                                            onCancel: () => {}
                                        });
                                    }}
                                >
                                    <i className="bi bi-safe2 me-2"></i>
                                    Liquidar Mes
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-3">
                <ul className="nav nav-tabs">
                    <li className="nav-item">
                        <button className={`nav-link ${vistaActiva === "general" ? "active text-dark fw-bold" : "text-secondary"}`} onClick={() => setVistaActiva("general")}>
                            Liquidación y Notificaciones
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${vistaActiva === "facturas" ? "active text-dark fw-bold" : "text-secondary"}`} onClick={() => setVistaActiva("facturas")}>
                            Facturas y Saldos
                        </button>
                    </li>
                </ul>

                {vistaActiva === "general" && (
                    <div className="card border-top-0 rounded-top-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0 text-muted">Resultados del Mes</h5>
                            </div>
                            <div className="mt-3">
                                <GdTable
                                    columns={columns}
                                    data={resultadosFiltrados}
                                    stickyHeader={true}
                                    height={600}
                                    headerHeight={50}
                                    showCsvExport={true}
                                    initialSort={[0, "asc"]}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {vistaActiva === "facturas" && (
                    <CobranzaFacturas fecha={fecha} />
                )}
            </div>

            <CobranzaDetalle
                visible={showDetalle}
                onClose={() => setShowDetalle(false)}
                periodo={fecha}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                pacienteData={resultadosFiltrados.find(r => r.paciente_id === detallePacienteID)}
                modoNotificacion={modoNotificacion}
                onNotificacionEnviada={(tipoStr) => {
                    setResultados(prev => prev.map(row => {
                        if (row.paciente_id === detallePacienteID) {
                            return { 
                                ...row, 
                                numero_notificacion: tipoStr,
                                fecha_ultima_notificacion: new Date().toISOString()
                            };
                        }
                        return row;
                    }));
                }}
            />

            <CobranzaAjusteModal 
                visible={showAjusteModal}
                onClose={() => setShowAjusteModal(false)}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                periodo={fecha}
                cargosIniciales={detalleCargos}
                descuentosIniciales={detalleDescuentos}
                observacionInicial={detalleObservacionAjuste}
                onSuccess={() => {
                    buscarCobranza();
                }}
            />

            <CobranzaAdelantoModal
                visible={showAdelantoModal}
                onClose={() => setShowAdelantoModal(false)}
                pacienteID={detallePacienteID}
                pacienteNombre={detallePacienteNombre}
                periodo={fecha}
                onSuccess={() => {
                    buscarCobranza(); // Recargamos para que traiga los nuevos números y turnos adelantados
                }}
            />

            <CartolaPacienteModal
                show={showCartolaModal}
                handleClose={() => setShowCartolaModal(false)}
                paciente={selectedPacienteCartola}
            />
        </div>
    );
};

export default Cobranza;
